import { CallRouteV1 } from '@frontend/api-call-route';
import { Trans, useTranslation } from '@frontend/i18n';
import { sentry } from '@frontend/tracking';
import { ConfirmationModal, ModalControlModalProps, Text } from '@frontend/design-system';
import { trackingId } from '../../../tracking';
import { NodeActionType } from '../types';
import { NodeTypeLabels } from './call-route-flow/data';

export const WarningModal = ({
  modalProps,
  nodeName,
  stepType,
  changingStepType,
  stepActionType,
  onConfirm,
}: {
  modalProps: ModalControlModalProps;
  stepActionType: NodeActionType;
  stepType?: CallRouteV1.Types.NodeType;
  changingStepType?: CallRouteV1.Types.NodeType;
  nodeName?: string;
  onConfirm: () => void;
}) => {
  const { t } = useTranslation('phone');

  const getStepTypeLabel = (type?: CallRouteV1.Types.NodeType) => {
    if (!type) {
      return '';
    }

    const label = NodeTypeLabels[type];

    return label;
  };

  const title = {
    add: t('Add Step?'),
    delete: t('Delete Step?'),
    edit: t('Change Step?'),
  };

  const getMessage = () => {
    if (
      (stepActionType === 'edit' && changingStepType === CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_NUMBER) ||
      (stepActionType === 'add' && stepType === CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_NUMBER)
    ) {
      // If we are showing the modal for a Forward Number step when adding or editing a node, then it is because it's
      // ring duration is set to "ring until answered".
      return t(
        'Adding a Forward Number with a "Ring until answered" ring duration in the middle of the Call Route will delete all subsequent steps.'
      );
    }

    if (stepActionType === 'delete') {
      return (
        <>
          {stepType === CallRouteV1.Types.NodeType.NODE_TYPE_BREAK_HOURS ? (
            <Text>{t('Deleting this break will remove all related steps.')}</Text>
          ) : (
            <Text>
              {t('Deleting a {{stepType}} step deletes all subsequent steps.', {
                stepType: getStepTypeLabel(stepType),
              })}
            </Text>
          )}
          <br />
          <Trans t={t}>
            <Text>
              Are you sure you want to delete <strong>{nodeName}</strong>?
            </Text>
          </Trans>
        </>
      );
    } else if (stepActionType === 'edit') {
      return (
        <Text>
          {t('Changing this step from a {{stepType}} to a {{changingStepType}} deletes all subsequent steps.', {
            stepType: getStepTypeLabel(stepType),
            changingStepType: getStepTypeLabel(changingStepType),
          })}
        </Text>
      );
    } else if (stepActionType === 'add') {
      return (
        <Text>
          {t('Adding a {{stepType}} step in the middle of the Call Route will delete all subsequent steps.', {
            stepType: getStepTypeLabel(stepType),
          })}
        </Text>
      );
    }

    sentry.error({
      error: 'Unhandled step action type in call-routes WarningModal',
      topic: 'phone-config',
      severityLevel: 'error',
      addContext: {
        name: 'getMessage',
        context: {
          stepActionType,
          stepType,
          changingStepType,
          nodeName,
        },
      },
    });
    // Default case for any other action type (should not happen)
    return t('Are you sure you want to proceed?');
  };

  const confirmLabel = {
    add: t('Yes, Add'),
    delete: t('Yes, Delete'),
    edit: t('Yes, Change'),
  };

  return (
    <ConfirmationModal
      {...modalProps}
      maxWidth={600}
      title={title[stepActionType]}
      message={getMessage()}
      cancelLabel={t('No, Cancel')}
      confirmLabel={confirmLabel[stepActionType]}
      destructive={stepActionType === 'delete'}
      cancelTrackingId={trackingId({
        context: 'setting',
        feature: 'call-routes',
        details: 'delete-associative-node::cancel-btn',
      })}
      confirmTrackingId={trackingId({
        context: 'setting',
        feature: 'call-routes',
        details: 'delete-associative-node::confirm-btn',
      })}
      onCancel={() => modalProps.onClose()}
      onConfirm={onConfirm}
    />
  );
};
